import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import {
  Home,
  Briefcase,
  User,
  Github,
  Mail,
  Moon,
  Sun,
  X,
  Sparkles,
} from "lucide-react";
import profileImage from "../assets/profile.jpg";

export default function Sidebar({ isMobile = false, closeMobileMenu = null, compact = false }) {
  const { isDark, toggleDarkMode } = useTheme();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  // Hover-to-expand only applies to the desktop instance; the mobile drawer
  // is an explicit open/close toggle and should always render at full width.
  const compactActive = compact && !isMobile;

  const navWrapperPad = compactActive ? "px-2" : "px-4";
  const navLinkPad = compactActive ? "px-3 py-3" : "px-4 py-3";
  const navGap = compactActive ? "gap-0 group-hover/sidebar:gap-3" : "gap-3";
  const navLabelClass = compactActive
    ? "inline-block max-w-0 opacity-0 overflow-hidden whitespace-nowrap transition-all duration-base ease-smooth group-hover/sidebar:max-w-[140px] group-hover/sidebar:opacity-100"
    : "whitespace-nowrap";

  const avatarSizeClass = compactActive
    ? "w-10 h-10 group-hover/sidebar:w-24 group-hover/sidebar:h-24"
    : "w-24 h-24";

  const profileTextWrapClass = compactActive
    ? "opacity-0 group-hover/sidebar:opacity-100"
    : "";

  return (
    <aside
      className={`${compactActive ? "w-20 hover:w-64" : "w-64"} group/sidebar flex flex-col border-r ${isMobile ? "h-screen" : "h-screen"} ${
        isDark
          ? "bg-background-dark border-border-dark"
          : "bg-background-sidebar border-border"
      } sticky top-0 overflow-y-auto overflow-x-hidden transition-[width] duration-base ease-smooth`}
    >
      {/* Avatar + Name + Tagline */}
      <div
        className={`${isMobile ? "p-4 pb-3" : compactActive ? "p-3 pb-2" : "p-6 pb-4"} relative`}
      >
        {/* Mobile Controls - Close button and Theme toggle on same line as profile */}
        {isMobile && (
          <div className="absolute top-4 right-4 flex flex-col gap-2">
            <button
              onClick={closeMobileMenu}
              className={`p-2 rounded-lg transition-all duration-base ${
                isDark
                  ? "text-gray-400 hover:text-white hover:bg-gray-700"
                  : "text-text-muted hover:text-text hover:bg-gray-100"
              }`}
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-lg transition-all duration-base ${
                isDark
                  ? "text-accent-dark hover:text-primary-dark hover:bg-border-dark"
                  : "text-text-muted hover:text-primary hover:bg-secondary/50"
              }`}
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        )}

        {/* Desktop Theme Toggle - Top Right */}
        {!isMobile && (
          <button
            onClick={toggleDarkMode}
            className={`absolute top-4 right-4 p-2 rounded-lg transition-all duration-base ${
              isDark
                ? "text-accent-dark hover:text-primary-dark hover:bg-border-dark"
                : "text-text-muted hover:text-primary hover:bg-secondary/50"
            }`}
            aria-label="Toggle dark mode"
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        )}

        <div
          className={`${avatarSizeClass} rounded-full overflow-hidden border-2 mb-4 transition-all duration-base ease-smooth ${
            isDark ? "border-primary-dark" : "border-primary"
          }`}
        >
          <img
            src={profileImage}
            alt="Mazikï profile"
            className="w-full h-full object-cover object-center"
          />
        </div>

        <div className={`${profileTextWrapClass} transition-opacity duration-base ease-smooth`}>
          <h2
            className={`text-2xl font-display font-semibold mb-1 whitespace-nowrap ${isDark ? "text-white" : "text-text"}`}
          >
            Mazikï
          </h2>
          <p
            className={`text-sm italic whitespace-nowrap ${isDark ? "text-gray-400" : "text-text-muted"}`}
          >
            Learning, Building and Growing
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav
        className={`${navWrapperPad} space-y-1 ${isMobile ? "mt-4" : "mt-8"}`}
        aria-label="Primary navigation"
      >
        <Link
          to="/"
          className={`group flex items-center ${navGap} ${navLinkPad} transition-all duration-base relative ${
            isActive("/")
              ? `font-medium ${isDark ? "text-white" : "text-text"}`
              : `${isDark ? "text-gray-400 hover:text-white" : "text-text-muted hover:text-text"}`
          }`}
        >
          <div
            className={`absolute left-0 top-0 h-full w-0.5 transition-transform duration-base ${
              isDark ? "bg-primary-dark" : "bg-primary"
            } ${isActive("/") ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"}`}
          ></div>
          <Home
            size={20}
            className={`shrink-0 transition-colors duration-base ${
              isActive("/")
                ? `${isDark ? "text-primary-dark" : "text-primary"}`
                : `${isDark ? "group-hover:text-primary-dark" : "group-hover:text-primary"}`
            }`}
          />
          <span className={navLabelClass}>Blogs</span>
        </Link>
        <Link
          to="/portfolio"
          className={`group flex items-center ${navGap} ${navLinkPad} transition-all duration-base relative ${
            isActive("/portfolio")
              ? `font-medium ${isDark ? "text-white" : "text-text"}`
              : `${isDark ? "text-gray-400 hover:text-white" : "text-text-muted hover:text-text"}`
          }`}
        >
          <div
            className={`absolute left-0 top-0 h-full w-0.5 transition-transform duration-base ${
              isDark ? "bg-primary-dark" : "bg-primary"
            } ${isActive("/portfolio") ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"}`}
          ></div>
          <Briefcase
            size={20}
            className={`shrink-0 transition-colors duration-base ${
              isActive("/portfolio")
                ? `${isDark ? "text-primary-dark" : "text-primary"}`
                : `${isDark ? "group-hover:text-primary-dark" : "group-hover:text-primary"}`
            }`}
          />
          <span className={navLabelClass}>Portfolio</span>
        </Link>
        <Link
          to="/about"
          className={`group flex items-center ${navGap} ${navLinkPad} transition-all duration-base relative ${
            isActive("/about")
              ? `font-medium ${isDark ? "text-white" : "text-text"}`
              : `${isDark ? "text-gray-400 hover:text-white" : "text-text-muted hover:text-text"}`
          }`}
        >
          <div
            className={`absolute left-0 top-0 h-full w-0.5 transition-transform duration-base ${
              isDark ? "bg-primary-dark" : "bg-primary"
            } ${isActive("/about") ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"}`}
          ></div>
          <User
            size={20}
            className={`shrink-0 transition-colors duration-base ${
              isActive("/about")
                ? `${isDark ? "text-primary-dark" : "text-primary"}`
                : `${isDark ? "group-hover:text-primary-dark" : "group-hover:text-primary"}`
            }`}
          />
          <span className={navLabelClass}>About</span>
        </Link>
      </nav>

      {/* Social Links - Always visible, more spacing on mobile */}
      <div className={`${isMobile ? "px-4 pb-4 mt-24" : compactActive ? "px-2 pb-6 mt-auto" : "px-6 pb-6 mt-auto"}`}>
        <div
          className={`flex justify-center items-center ${
            compactActive
              ? "flex-col gap-3 group-hover/sidebar:flex-row group-hover/sidebar:gap-8"
              : "flex-row gap-8"
          }`}
        >
          <a
            href="https://github.com/Mazikiii"
            target="_blank"
            rel="noopener noreferrer"
            className={`shrink-0 transition-colors duration-fast ${
              isDark
                ? "text-gray-400 hover:text-primary-dark"
                : "text-text-muted hover:text-primary"
            }`}
            aria-label="GitHub"
          >
            <Github size={20} />
          </a>
          <a
            href="https://x.com/Ikizam_"
            target="_blank"
            rel="noopener noreferrer"
            className={`shrink-0 transition-colors duration-fast ${
              isDark
                ? "text-gray-400 hover:text-primary-dark"
                : "text-text-muted hover:text-primary"
            }`}
            aria-label="X (Twitter)"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a
            href="mailto:majiboye4@protonmail.com"
            className={`shrink-0 transition-colors duration-fast ${
              isDark
                ? "text-gray-400 hover:text-primary-dark"
                : "text-text-muted hover:text-primary"
            }`}
            aria-label="Email"
          >
            <Mail size={20} />
          </a>
          <Link
            to="/library"
            className={`shrink-0 transition-colors duration-fast opacity-40 hover:opacity-100 ${
              isDark
                ? "text-gray-400 hover:text-primary-dark"
                : "text-text-muted hover:text-primary"
            }`}
            aria-label="Library"
          >
            <Sparkles size={20} />
          </Link>
        </div>
      </div>
    </aside>
  );
}
